import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.#setupMobileDotWindow();
  }

  #controller = new AbortController();
  #dotsObserver;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#dotsObserver?.disconnect();
  }

  /**
   * Keeps a reference-style three-dot window in sync with galleries containing
   * more than three media items. The selected dot stays centred while possible,
   * then moves to the final position at the end of the gallery.
   */
  #setupMobileDotWindow = () => {
    const controls = this.querySelector('.media-gallery__mobile-controls .slideshow-controls__dots');
    if (!(controls instanceof HTMLElement)) return;

    const buttons = Array.from(controls.querySelectorAll('button'));
    const windowSize = Math.min(3, buttons.length);

    const updateWindow = () => {
      const selectedIndex = Math.max(
        0,
        buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true')
      );
      const windowStart = Math.max(0, Math.min(selectedIndex - 1, buttons.length - windowSize));

      buttons.forEach((button, index) => {
        const item = button.closest('li');
        if (!(item instanceof HTMLElement)) return;

        if (index >= windowStart && index < windowStart + windowSize) {
          item.removeAttribute('data-fenza-dot-hidden');
        } else {
          item.setAttribute('data-fenza-dot-hidden', '');
        }
      });
    };

    updateWindow();
    this.#dotsObserver = new MutationObserver(updateWindow);
    this.#dotsObserver.observe(controls, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-selected'],
    });
  };

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
